/**
 * Large-CSV fragments (#981) — constants shared by the server (routing, fragmenting) and the client.
 * Plan: docs/plans/2026-10-22_LARGE_CSV_FRAGMENTS_981.md.
 */
import { isCsvOrTsvName } from './file-extensions.js'

/** A `.csv` / `.tsv` strictly larger than this is a "large CSV". One constant, read by every side. */
export const LARGE_CSV_THRESHOLD_BYTES = 5 * 1024 * 1024

/**
 * A fragment is cut at the first record boundary after ~this many source bytes — or sooner, at
 * {@link LARGE_CSV_FRAGMENT_MAX_ROWS} rows or {@link LARGE_CSV_FRAGMENT_MAX_CELLS} cells (#981 Task 13). A fragment's
 * live room costs heap by ROWS and CELLS, not bytes (~1.8 KB a row + ~460 B a cell): bytes alone let a 1 MB fragment
 * of a 1-column CSV cost ~460 MB. Measured at these bounds (one room's doc, retained after GC, seeded / loaded from its
 * stored row): 1 column × 2,000 rows 3.9 / 4.2 MB, 4 × 2,000 5.4 / 6.4 MB, 8 × 2,000 7.6 / 9.5 MB (the worst: both
 * row bounds at once), 20 × 800 6.3 / 8.3 MB, 50 × 320 5.5 / 7.5 MB.
 */
export const LARGE_CSV_FRAGMENT_TARGET_BYTES = 128 * 1024

/** At most this many records in one fragment (#981 Task 13: a narrow CSV's fragment is bounded by rows, not bytes). */
export const LARGE_CSV_FRAGMENT_MAX_ROWS = 2_000

/**
 * At most this many cells (fields, summed over its records) in one fragment, unless one record alone holds more
 * (#981 Task 13: a wide CSV of short values — 20 one-digit columns — reaches neither the byte nor the row bound
 * before its room costs ~15 MB).
 */
export const LARGE_CSV_FRAGMENT_MAX_CELLS = 16_000

/** Master switch for routing large CSVs to the fragment editor. On since Task 18 (#981). */
export const LARGE_CSV_ROUTING_ENABLED = true

/** True iff `name` is a csv/tsv (any case) and `size` is known and over {@link LARGE_CSV_THRESHOLD_BYTES}. */
export function isLargeCsv(name: string, size: number | null): boolean {
  return size !== null && isCsvOrTsvName(name) && size > LARGE_CSV_THRESHOLD_BYTES
}
